// TRADE DETAIL (UI rebuild batch 2) — one deal in the kit's DetailSheet over the Trades register, drawn as Astra's
// trade-detail (operator's ruling, 26 Oct 2026: Astra's mockup is the default, safety checks in their lightest form).
//
//   · the big figure (what the customer receives) and the state, and under it what REMAINS to settle, one line;
//   · the facts: party, reference, what the customer pays, the rate, when it was agreed (and how a settled trade closed,
//     or a cheque's two steps — the actual settlement Astra keeps visible);
//   · "More" for the rest (via, beneficiary, spread, who booked it, the cheque's other facts, the timeline);
//   · one gold primary, Settle, on a trade still awaiting: it opens Record money prefilled for the open leg (party,
//     direction, asset, amount — the params Record money reads today); Mark settled stays the guarded quiet action;
//   · Void alone at the left of the footer, its reason asked for only once it is pressed.
//
// The logic is the deal panel's (components/DealSlideOver.tsx), not re-derived: the same reads under the same keys (the
// cheque read, the settle-later read for an awaiting trade), its helpers, SettleButton, and the void flow word for word —
// the ten-character reason, the LIFO refusal, the rebuild. NOTHING IS CLAIMED FROM A READ THAT DID NOT COME: an awaiting
// trade's legs say "Reading" or the failure with Try again, never "settled"; Settle prefills an amount only from a
// successful read of exactly one open leg (SettleButton's rule: two open legs get no shortcut).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, refreshMoneyViews, type TradeRow } from '../../../lib/api';
import { displayAsset, fmtAmount, fmtDate, fmtMoney, fmtRate } from '../../../lib/format';
import { partyName, rodeSentence } from '../../../lib/naming';
import { spreadAsset } from '../../../lib/workflow';
import { ApiError } from '../../../lib/errors';
import { SettleButton, legWords } from '../../SettleButton';
import { Pill } from '../../Pill';
import { CopyRef } from '../../CopyRef';
import { ErrorLine, WarnLine } from '../../ErrorLine';
import { rateQuote } from '../../Ledger';
import {
  isChequeDeal, tradeKind, lifecycleLabel, lifecycleTone, operatorLabel, viaWords,
  VOID_REASONS, VOID_REASON_MIN, flippedTrade,
} from '../../DealSlideOver';
import { DetailSheet, Fact, Label, Money, Pending, ReadFailed, TAP } from '../kit';

export function TradeDetail({ deal, onClose }: { deal: TradeRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [confirmVoid, setConfirmVoid] = useState(false);
  const [voidChip, setVoidChip]       = useState<(typeof VOID_REASONS)[number] | null>(null);
  const [voidReason, setVoidReason]   = useState('');
  const [voiding, setVoiding]         = useState(false);
  const [voidError, setVoidError]     = useState<string | null>(null);
  const [voidBlocked, setVoidBlocked] = useState(false);   // the plain void refused: newer trades on the book
  // The chip and the free text together are the reason the books keep (sweep item 32): ten characters at least.
  const voidWords = [voidChip, voidReason.trim()].filter(Boolean).join(' — ');
  const reasonOk  = voidWords.length >= VOID_REASON_MIN;
  // (The confirm cannot outlive its deal: Trades mounts this sheet keyed by the deal id.)

  const isMc = isChequeDeal(deal);
  const mc = useQuery({
    queryKey: ['mc', 'deal', deal.deal_id],
    queryFn:  () => api.getMcDeal(deal.deal_id),
    enabled:  isMc,
    retry:    false,
    staleTime: 60_000,
  });
  const md = mc.data?.deal;

  const kind = tradeKind(deal);
  // The log's own grammar: never spelled differently from the row behind. Nothing filtered by relevance — evidence.
  const party    = partyName({ display_name: deal.party_name, slug: deal.party_slug });
  const merchant = deal.merchant_name || deal.merchant_slug ? partyName({ display_name: deal.merchant_name, slug: deal.merchant_slug }) : '';
  // As the deal panel: MC and passthrough have no implicit settled state.
  const lifecycle = md?.lifecycle ?? deal.lifecycle ?? (kind === 'mc' ? null : 'settled');
  const closed = lifecycle === 'settled' ? (deal.notes ?? null) : null;
  const awaiting = lifecycle === 'awaiting';

  // WHY AN AWAITING TRADE IS STILL OPEN — the read /settle and the party page make, under the same key.
  const settle = useQuery({
    queryKey: ['settlements', 'dubaiotc'],
    queryFn:  () => api.settlements('dubaiotc'),
    enabled:  awaiting,
    retry:    false,
    staleTime: 15_000,
  });
  const legs = settle.data?.parties.flatMap((p) => p.deals).find((d) => d.deal_id === deal.deal_id)?.legs ?? null;
  // The sweep runs on that read and may have closed THIS trade a moment ago; `swept` is the only place that says so.
  const sweptNow = settle.data?.swept.find((s) => s.deal_id === deal.deal_id) ?? null;

  // SETTLE → Record money, prefilled for the ONE open leg; with both open (or the legs unread) it carries the party only.
  const partyQ = `party=${encodeURIComponent(deal.party_slug)}`;
  const openLeg = legs && !settle.isError && legs.take.settled !== legs.give.settled
    ? (legs.take.settled ? { dir: 'out', asset: legs.give.asset, open: legs.give.open } : { dir: 'in', asset: legs.take.asset, open: legs.take.open })
    : null;
  const settleHref = openLeg
    ? `/record?${partyQ}&dir=${openLeg.dir}&asset=${encodeURIComponent(openLeg.asset)}&amount=${openLeg.open}`
    : `/record?${partyQ}`;

  // The timeline — cheques and voids only (for any other deal it repeated the Booked row).
  const timeline: Array<{ key: string; time: string; text: string; tone: string }> = [];
  if (lifecycle === 'voided') timeline.push({ key: 'void', time: '—', tone: 'bg-tg-hint', text: md?.void_reason ? `Deal voided · ${md.void_reason}` : 'Deal voided' });
  if (isMc && md) {
    if (md.mc_delivered_at) timeline.push({ key: 'delivered', time: fmtDate(md.mc_delivered_at), text: 'Cheque delivered', tone: 'bg-pos' });
    else if (lifecycle !== 'voided') timeline.push({ key: 'delivered', time: 'Not yet', text: 'Cheque delivery', tone: 'bg-warn' });
    if (md.mc_seller_paid_at) timeline.push({ key: 'seller-paid', time: fmtDate(md.mc_seller_paid_at), text: 'Seller paid', tone: 'bg-pos' });
    else if (lifecycle !== 'voided') timeline.push({ key: 'seller-paid', time: 'Not yet', text: 'Seller payment', tone: 'bg-warn' });
  }
  timeline.push({ key: 'booked', time: fmtDate(deal.booked_at), text: `Deal booked · ${operatorLabel(deal.booked_by_admin)}`, tone: 'bg-pos' });

  const via = viaWords(deal);
  const flipped = flippedTrade(deal);
  const partyHref = `/parties/${encodeURIComponent(deal.party_slug)}?entity=dubaiotc`;

  return (
    <DetailSheet
      onClose={onClose}
      title="Trade detail"
      footer={
        <>
          {/* ── Void's reason, asked for only once Void is pressed. A reason is REQUIRED: an unexplained reversal is
              indistinguishable from a mistake six months later. */}
          {confirmVoid && (
            <div className="mb-4 space-y-3 rounded-lg border border-neg/25 bg-neg-bg p-4">
              <h3 className="text-body font-medium">Void this trade?</h3>
              <p className="text-meta text-tg-hint">Reverses every leg; both entries stay on the record.</p>
              <div className="flex flex-wrap gap-2">
                {VOID_REASONS.map((c) => (
                  <button key={c} type="button" className={`chip ${voidChip === c ? 'chip-on' : ''}`} onClick={() => setVoidChip(voidChip === c ? null : c)}>
                    {c}
                  </button>
                ))}
              </div>
              <label className="block">
                <span className="sr-only">Reason</span>
                <input autoFocus value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
                  placeholder={voidChip === 'Duplicate' ? 'DEAL-2026-09-12-…' : ''} className="input w-full" />
              </label>
              {!reasonOk && <p className="text-meta text-tg-hint">Ten characters at least — chip and words together.</p>}
              {/* Booked the wrong way round: the void's usual sequel. Once voided the trade leaves the list, so this is its door. */}
              <Link to={flipped.href} title={flipped.words} className={`block ${TAP}`} onClick={onClose}>Book it again the other way →</Link>
              <ErrorLine>{voidError}</ErrorLine>
              {/* ONE RED BUTTON AT A TIME: after the LIFO refusal the plain void cannot succeed, so only the rebuild is red. */}
              {voidBlocked && <WarnLine>Rebuilding replays the newer trades without this one.</WarnLine>}
              <div className="flex flex-wrap justify-end gap-2">
                <button type="button" className="btn-secondary px-3 text-body" onClick={() => { setConfirmVoid(false); setVoidError(null); setVoidBlocked(false); }}>
                  Cancel
                </button>
                {!voidBlocked && (
                  <button
                    type="button"
                    disabled={!reasonOk || voiding}
                    className="inline-flex min-h-11 items-center rounded-lg bg-neg px-3 text-body text-white disabled:opacity-40 lg:min-h-10"
                    onClick={async () => {
                      setVoiding(true); setVoidError(null);
                      try {
                        await api.voidTrade(deal.deal_id, voidWords);
                        // EVERY money view, not the trades alone: the sheet opens on a party's page and the forms' Last 10 too, whose
                        // balances, statement and settle-later list the reversal changes (Astra, 1 Nov 2026).
                        refreshMoneyViews(qc);
                        setConfirmVoid(false); setVoidChip(null); setVoidReason(''); onClose();
                      } catch (e) {
                        // The route's own words: "not_last_event" names the deal that has to go first.
                        setVoidError(e instanceof Error ? e.message : 'Void failed');
                        setVoidBlocked(e instanceof ApiError && e.code === 'not_last_event');
                      } finally { setVoiding(false); }
                    }}
                  >
                    {voiding ? 'Voiding…' : 'Void trade'}
                  </button>
                )}
                {/* Newer trades on the same cost book: the rebuild replays the book without this one. */}
                {voidBlocked && (
                  <button
                    type="button"
                    disabled={!reasonOk || voiding}
                    className="inline-flex min-h-11 items-center rounded-lg bg-neg px-3 text-body text-white disabled:opacity-40 lg:min-h-10"
                    onClick={async () => {
                      setVoiding(true); setVoidError(null);
                      try {
                        await api.rebuildV3({ void_deal_ids: [deal.deal_id], void_reason: voidWords, dry_run: false });
                        refreshMoneyViews(qc);   // the trades and the cost book among them
                        setConfirmVoid(false); setVoidChip(null); setVoidReason(''); setVoidBlocked(false); onClose();
                      } catch (e) {
                        setVoidError(e instanceof Error ? e.message : 'Rebuild failed');
                      } finally { setVoiding(false); }
                    }}
                  >
                    {voiding ? 'Voiding…' : 'Void and rebuild the book'}
                  </button>
                )}
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            {/* VOID STANDS APART, at the left. Not on a carried-in trade (no v3 legs to reverse), a void, or a cheque. */}
            {lifecycle !== 'voided' && lifecycle !== 'absorbed' && !isMc && !confirmVoid && (
              <button type="button" onClick={() => setConfirmVoid(true)} disabled={voiding}
                className="inline-flex min-h-11 items-center rounded-lg px-3 text-body text-neg hover:bg-neg-bg disabled:opacity-50 lg:min-h-10">
                Void
              </button>
            )}
            <div className="flex-1" />
            {awaiting && !sweptNow && (
              <>
                {/* The guarded override, quiet: the BFF refuses while a leg is open and the panel names the shortfall. */}
                <SettleButton dealId={deal.deal_id} partySlug={deal.party_slug ?? undefined} onDone={onClose} panelClassName="basis-full order-first" />
                <Link to={settleHref} className="btn-primary px-5 text-body">Settle</Link>
              </>
            )}
          </div>
        </>
      }
    >
      {/* ── the agreement: what the customer receives, its state, and what remains */}
      <section className="border-b border-stroke pb-5">
        <Label>{isMc ? 'Cheque' : 'Customer receives'}</Label>
        <div className="mt-2 break-words"><Money size="figure" value={deal.amount_give} asset={deal.asset_give} /></div>
        {lifecycle && <div className="mt-3"><Pill tone={lifecycleTone(lifecycle)}>{lifecycleLabel(lifecycle)}</Pill></div>}
        {awaiting && (
          <div className="mt-3 text-body">
            {settle.isError ? <ReadFailed tone="bad" what="the legs" error={settle.error} onRetry={() => { void settle.refetch(); }} />
              : sweptNow ? <p className="text-pos">Closed by the sweep just now{rodeSentence(sweptNow.rode) ? ` — ${rodeSentence(sweptNow.rode)}` : ''}.</p>
              : legs ? (
                // WHAT REMAINS, from the customer's side like the figures around it (Astra, 1 Nov 2026: the desk's "to receive"
                // beside "Customer pays" read as a second, different amount). An open leg says its remainder, figure then unit;
                // a closed one how it closed.
                <div>
                  <Label>Remaining</Label>
                  {(['take', 'give'] as const).map((side) => {
                    const l = legs[side];
                    return (
                      <div key={side} className="flex items-baseline justify-between gap-4 py-1">
                        <span className="text-tg-hint">{side === 'take' ? 'Customer pays' : 'Customer receives'}</span>
                        {l.settled ? <span className="text-pos">{legWords(l, side)}</span>
                          : <span className="text-warn"><Money value={l.open} asset={l.asset} /></span>}
                      </div>
                    );
                  })}
                </div>
              )
              : <Pending inline what="Reading the legs…" />}
          </div>
        )}
        {/* A voided trade's most common sequel is the same trade the other way — one link, nothing retyped. */}
        {lifecycle === 'voided' && !isMc && (
          <Link to={flipped.href} title={flipped.words} className={`mt-3 block ${TAP}`}>Book it again the other way →</Link>
        )}
      </section>

      {/* ── the facts */}
      <div className="pt-2">
        <Fact label="Party"><Link to={partyHref} className="btn-link">{party || '—'}</Link></Fact>
        <Fact label="Reference"><CopyRef value={deal.deal_id} className="text-body" /></Fact>
        <Fact label={isMc ? 'Deposit' : 'Customer pays'}><span className="amt">{fmtMoney(deal.amount_take, deal.asset_take)}</span></Fact>
        {!isMc && deal.rate != null && deal.rate !== '' && <Fact label="Rate"><span className="amt">{fmtRate(deal.rate, rateQuote(deal))}</span></Fact>}
        <Fact label="Agreed">{fmtDate(deal.booked_at)}</Fact>   {/* Dubai time, as every time in the Books; the zone is not printed */}
        {closed && <Fact label="Closed">{closed}</Fact>}
        {isMc && (mc.isError
          ? <div className="py-3"><ReadFailed tone="bad" what="the cheque" error={mc.error} onRetry={() => { void mc.refetch(); }} /></div>
          : !md ? <div className="py-3 text-body"><Pending inline what="Reading the cheque…" /></div>
          : (
            <>
              <Fact label="Seller paid">{md.mc_seller_paid_at ? fmtDate(md.mc_seller_paid_at) : <span className="text-tg-hint">not yet</span>}</Fact>
              <Fact label="Delivered">{md.mc_delivered_at ? fmtDate(md.mc_delivered_at) : <span className="text-tg-hint">not yet</span>}</Fact>
            </>
          ))}
      </div>

      {/* ── the rest, on request */}
      <details className="mt-4 border-t border-stroke-soft pt-2">
        <summary className="flex min-h-11 cursor-pointer items-center text-body text-tg-hint hover:text-tg-text lg:min-h-10">More</summary>
        <div className="pt-1">
          {lifecycle === 'absorbed' && <p className="py-3 text-body text-tg-hint">Carried into the 1 Sept opening box — nothing here to reverse.</p>}
          {via && <Fact label="Via">{via}{merchant && <span className="text-tg-hint"> · {merchant}</span>}</Fact>}
          {deal.beneficiary && <Fact label="Beneficiary">{deal.beneficiary}</Fact>}
          {deal.spread_amount != null && Number(deal.spread_amount) !== 0 && (
            <Fact label="Spread"><span className="amt">{fmtMoney(deal.spread_amount, spreadAsset(deal))}</span></Fact>
          )}
          {isMc && md && (
            <>
              <Fact label="Seller">{partyName({ display_name: md.mc_seller_display_name ?? deal.merchant_name, slug: md.mc_seller_slug })}</Fact>
              <Fact label="Face amount"><span className="amt">{fmtAmount(md.mc_face_amount, md.asset_take)}</span> {displayAsset(md.asset_take)}</Fact>
              {md.void_reason && <Fact label="Void reason">{md.void_reason}</Fact>}
              {md.notes && <Fact label="Notes"><span className="whitespace-pre-wrap">{md.notes}</span></Fact>}
            </>
          )}
          <Fact label="Booked by"><span className="text-tg-hint">{operatorLabel(deal.booked_by_admin)}</span></Fact>
          {timeline.length > 1 && (
            <ul className="mt-4 border-l border-stroke pl-4">
              {timeline.map((ev) => (
                <li key={ev.key} className="relative pb-4 pl-3 last:pb-0">
                  <span aria-hidden className={`absolute -left-[21px] top-1 h-2 w-2 rounded-full ring-[3px] ring-panel ${ev.tone}`} />
                  <div className="amt text-meta text-tg-hint">{ev.time}</div>
                  <div className="mt-1 text-meta">{ev.text}</div>
                </li>
              ))}
            </ul>
          )}
          {/* A delivery charge that belongs to this deal (2 Oct 2026): the link carries party and deal. */}
          <Link to={`/record?mode=charge&${partyQ}&deal=${encodeURIComponent(deal.deal_id)}`} className={`mt-3 block ${TAP}`} onClick={onClose}>
            Add delivery charge →
          </Link>
        </div>
      </details>
    </DetailSheet>
  );
}
